import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useSessionStore } from '../session/context.tsx'
import { ThemeSwitch } from '../theme/theme-controls.tsx'
import { BrandMark } from './brand-mark.tsx'
import { PRODUCT_NAME } from './document-title.ts'
import { EnvironmentBadge } from './environment-badge.tsx'

/**
 * The chrome, on every signed-in page: the product name, the **environment**, the two
 * places this application goes, who is signed in, and Sign out. No sidebar, no footer
 * (§5.1) — and no breadcrumb trail in the masthead: a tenant's own pages carry their back
 * link and tabs in the page, in `TenantHeader`.
 *
 * **"Signed in as" is the database's answer, not a token's claim.** The shell already asks
 * `GET /platform/auth/session` who this browser is (`AppShell`), and hands the nickname
 * down; until that read has landed the masthead says nothing there, rather than a
 * placeholder that looks like a name.
 *
 * **No "Signing keys" link.** That screen, and the platform-wide signing keys it managed,
 * are gone — a tenant's build key is rotated from its own page instead
 * (`docs/DECISIONS.md`, "The build identifier is a plain rotatable key on the tenant row,
 * not a signed, kid-rotated token"). There is nothing platform-wide left for this masthead
 * to link to.
 *
 * The environment is a **word**, not only a colour (§21), and it is stated always — a
 * failed read says *environment unknown* rather than guessing (§5.1).
 *
 * **P09 (2026-10-15) added the theme switch** — three icons, System / Light / Dark — between who is
 * signed in and Sign out. It is the second way to choose a theme; Your account has the first, and
 * both are one preference (`theme/`). It is hidden below 40 rem, where the masthead has no room for
 * it and Your account does.
 */
export type MastheadProps = {
  /** The signed-in administrator's nickname, once the identity read has landed. */
  readonly nickname?: string | undefined
}

export function Masthead({ nickname }: MastheadProps = {}) {
  const store = useSessionStore()
  const [signingOut, setSigningOut] = useState(false)

  const signOut = async () => {
    setSigningOut(true)
    // Sign out revokes the session server-side, not only in the browser (§4.4). The
    // store ends the local session either way, so an unreachable API cannot strand
    // somebody signed in.
    //
    // It deliberately does **not** navigate. `AppShell` already redirects the moment a
    // session ends — that is how a session ending underneath an open page works — and a
    // second navigation here raced with it over `?next=`. Whichever landed last decided
    // where the *following* sign-in went, so a sign-out from `/account` could resume
    // `/account`, which is exactly what "a deliberate sign-out is not an interruption"
    // says must not happen. One redirect, in one place, that knows why the session ended.
    await store.signOut()
  }

  return (
    <header className="masthead">
      {/*
        Exact, like every other link back to the list: a tenant's pages are under /tenants,
        and the product name is not "the page you are on" there. The Tenants link in the
        section nav is the one that stays current across them.
      */}
      <Link to="/tenants" className="masthead-product" activeOptions={{ exact: true }}>
        <BrandMark size={26} />
        {PRODUCT_NAME}
      </Link>
      <EnvironmentBadge />
      <nav aria-label="Sections">
        <Link to="/tenants">Tenants</Link>
        <Link to="/account">Your account</Link>
      </nav>
      <div className="masthead-end">
        {nickname === undefined ? null : (
          <span className="masthead-who">
            Signed in as <code>{nickname}</code>
          </span>
        )}
        <ThemeSwitch />
        <button
          type="button"
          className="button"
          // Every mutation-bound control is inert while its own work is in flight —
          // **CF3** and **HF12** are the two ways to get that wrong (§5.3).
          disabled={signingOut}
          aria-busy={signingOut}
          onClick={() => {
            void signOut()
          }}
        >
          Sign out
        </button>
      </div>
    </header>
  )
}
